/**
 * Compute position and max height for a position:fixed popup anchored to a trigger.
 * Uses viewport coordinates only — do not add window.scrollY (fixed is relative to the viewport).
 *
 * @param {DOMRect} triggerRect - from element.getBoundingClientRect()
 * @param {object} [options]
 * @param {number} [options.estimatedWidth=820] - used for horizontal clamping
 * @param {number} [options.gap=8] - gap between trigger and popup
 * @param {number} [options.margin=12] - viewport inset
 * @param {number} [options.minHeight=160] - minimum usable popup height (px)
 * @param {boolean} [options.preferAbove=false] - open above the trigger whenever it has room for a usable popup
 * @param {number} [options.maxHeightCap] - upper bound for max-height (defaults to min(65vh, vh - 2*margin))
 */
export function computeFixedPopupPosition(triggerRect, options = {}) {
  const gap = options.gap ?? 8;
  const margin = options.margin ?? 12;
  const estimatedWidth = options.estimatedWidth ?? 820;
  const minH = options.minHeight ?? 160;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

  const cap =
    options.maxHeightCap != null
      ? Math.min(options.maxHeightCap, vh - 2 * margin)
      : Math.min(Math.round(vh * 0.65), vh - 2 * margin);

  let left = triggerRect.left;
  if (left + estimatedWidth > vw - margin) {
    left = Math.max(margin, triggerRect.right - estimatedWidth);
  }
  left = Math.max(margin, Math.min(left, vw - estimatedWidth - margin));

  const belowTop = triggerRect.bottom + gap;
  const spaceBelow = vh - belowTop - margin;
  const spaceAbove = triggerRect.top - gap - margin;

  const fitBelow = Math.min(cap, Math.max(0, spaceBelow));
  const fitAbove = Math.min(cap, Math.max(0, spaceAbove));

  let top;
  let maxHeightPx;

  if (options.preferAbove && fitAbove >= Math.max(minH, 300)) {
    maxHeightPx = fitAbove;
    top = triggerRect.top - gap - maxHeightPx;
  } else if (fitBelow >= minH && fitBelow >= fitAbove) {
    top = belowTop;
    maxHeightPx = fitBelow;
  } else if (fitAbove >= minH) {
    maxHeightPx = fitAbove;
    top = triggerRect.top - gap - maxHeightPx;
  } else if (spaceBelow >= spaceAbove) {
    top = belowTop;
    maxHeightPx = Math.max(120, Math.min(cap, Math.max(spaceBelow, 0)));
  } else {
    maxHeightPx = Math.max(120, Math.min(cap, Math.max(spaceAbove, 0)));
    top = triggerRect.top - gap - maxHeightPx;
  }

  if (top < margin) {
    top = margin;
    maxHeightPx = Math.min(maxHeightPx, vh - top - margin);
  }
  if (top + maxHeightPx > vh - margin) {
    maxHeightPx = Math.max(120, vh - margin - top);
  }

  maxHeightPx = Math.max(120, Math.round(maxHeightPx));

  return { top, left, maxHeightPx };
}
